/**
 * 감사로그 한 건을 **사람이 읽는 말**로 옮긴다.
 *
 * 서버가 남기는 것은 `display.update` 라는 동작 이름과 변경 전후 스냅샷이다.
 * 그대로 늘어놓으면 "무엇이 바뀌었나" 에 답하지 못한다 — 화면 상세의 변경 이력을
 * 여는 사람은 개발자가 아니라 "어제까지 잘 나오던 게 왜 바뀌었지" 를 묻는 운영자다.
 *
 * 그래서 두 가지를 한다. 필드 이름을 사람 말로 바꾸고(`assignment` → 편성),
 * **값을 뜻으로 바꾼다**(ObjectId → 스케줄 이름, `canary` → 카나리).
 *
 * 모르는 필드·모르는 동작은 **버리지 않는다.** 서버에 새 동작이 늘 생기고, 그때
 * 이력이 조용히 비어 버리면 감사로그를 보는 이유가 사라진다 — 사람 말로 옮기지
 * 못한 것은 원래 값 그대로라도 보여준다.
 */
import type { AuditEntry } from './api/client.js';

/** id 를 이름으로. 콘솔이 이미 읽어 둔 목록에서 찾는다. 못 찾으면 undefined. */
export type NameLookup = (kind: 'playlist' | 'schedule' | 'group', id: string) => string | undefined;

/** 필드 이름을 사람 말로. */
const FIELD_LABEL: Record<string, string> = {
  name: '이름',
  location: '위치',
  /*
   * `region` 은 우리말 이름을 주지 않는다 — 콘솔이 묶고 거르고 옮기는 축은 `location`
   * 하나이고(목록·대시보드·모니터링 벽), 그것을 "위치" 라고 부른다. 여기에 '지역' 을
   * 되살리면 한 줄짜리 이력에 위치가 둘 있는 셈이 된다. 등록할 때 API 로 이 값이 들어온
   * 이력은 여전히 보여준다 — 옮기지 못한 이름은 원래 키(`region`)로 남는다.
   */
  group_id: '그룹',
  assignment: '편성',
  channel: '업데이트 채널',
  pinned_version: '버전 고정',
  timezone: '기준 시간대',
  orientation: '화면 방향',
  rotation: '화면 회전',
  tags: '태그',
  hidden: '목록 표시',
  note: '메모',
};

/**
 * 읽는 순서.
 *
 * 눈에 먼저 들어와야 하는 것은 "무엇이 나가는가"(편성)이지 태그가 아니다.
 * 여기 없는 필드는 뒤에 원래 순서대로 붙는다.
 */
const FIELD_ORDER = [
  'assignment',
  'group_id',
  'name',
  'location',
  'region',
  'timezone',
  'orientation',
  'channel',
  'pinned_version',
  'tags',
  'hidden',
  'note',
];

function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

/**
 * id 를 이름으로. 못 찾으면 id 뒷자리를 남긴다.
 *
 * 지워진 플레이리스트·아직 읽지 않은 목록 어느 쪽인지 콘솔은 구분하지 못한다.
 * "삭제됨" 이라고 단정하면 거짓말이 될 수 있어, 되짚을 수 있는 뒷자리만 준다.
 * 무엇의 id 인지는 앞에 붙는 필드 이름·종류가 이미 말하고 있다.
 */
function refText(kind: 'playlist' | 'schedule' | 'group', id: unknown, lookup?: NameLookup): string {
  if (typeof id !== 'string' || !id) return '없음';
  const name = lookup?.(kind, id);
  return name ? `"${name}"` : `…${id.slice(-6)}`;
}

/** 편성 한 덩어리를 한 줄로. */
function assignmentText(value: unknown, lookup?: NameLookup): string {
  if (!value || typeof value !== 'object') return '없음';
  const assignment = value as { kind?: string; playlist_id?: unknown; schedule_id?: unknown };
  if (assignment.kind === 'playlist') return `플레이리스트 ${refText('playlist', assignment.playlist_id, lookup)}`;
  if (assignment.kind === 'schedule') return `스케줄 ${refText('schedule', assignment.schedule_id, lookup)}`;
  return '없음';
}

/** 시각은 이력 안에서만 뜻이 통한다 — 상대 시각이 아니라 그 자리에 적힌 절대 시각으로. */
function timeText(value: unknown): string {
  if (typeof value !== 'string' || !value) return '없음';
  const at = Date.parse(value);
  if (Number.isNaN(at)) return value;
  return new Date(at).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** 값 하나를 사람 말로. */
export function valueText(field: string, value: unknown, lookup?: NameLookup): string {
  if (value === null || value === undefined || value === '') return '없음';
  switch (field) {
    case 'assignment':
      return assignmentText(value, lookup);
    case 'group_id':
      return refText('group', value, lookup);
    case 'playlist_id':
      return refText('playlist', value, lookup);
    case 'schedule_id':
      return refText('schedule', value, lookup);
    case 'channel':
      return value === 'canary' ? '카나리' : '안정';
    case 'orientation':
      return value === 'portrait' ? '세로' : '가로';
    case 'pinned_version':
      return `v${String(value)}`;
    case 'hidden':
      return value ? '목록에서 숨김' : '목록에 보임';
    case 'expires_at':
    case 'until':
      return timeText(value);
    default:
      break;
  }
  if (typeof value === 'boolean') return value ? '예' : '아니오';
  if (Array.isArray(value)) return value.length > 0 ? value.map((item) => String(item)).join(', ') : '없음';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** 바뀐 필드를 읽는 순서대로. */
function changedFields(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => !same(before[key], after[key]),
  );
  return keys.sort((a, b) => {
    const ia = FIELD_ORDER.indexOf(a);
    const ib = FIELD_ORDER.indexOf(b);
    return (ia === -1 ? FIELD_ORDER.length : ia) - (ib === -1 ? FIELD_ORDER.length : ib);
  });
}

/** 태그는 "전 → 후" 보다 **들고 난 것**이 읽기 쉽다. */
function tagLines(before: unknown, after: unknown): string[] {
  const from = Array.isArray(before) ? before.map(String) : [];
  const to = Array.isArray(after) ? after.map(String) : [];
  const added = to.filter((tag) => !from.includes(tag));
  const removed = from.filter((tag) => !to.includes(tag));
  const lines: string[] = [];
  if (added.length > 0) lines.push(`태그 추가: ${added.join(', ')}`);
  if (removed.length > 0) lines.push(`태그 제거: ${removed.join(', ')}`);
  return lines;
}

function diffLines(entry: AuditEntry, lookup?: NameLookup): string[] {
  const before = asRecord(entry.before);
  const after = asRecord(entry.after);
  const lines: string[] = [];

  for (const field of changedFields(before, after)) {
    if (field === 'tags') {
      lines.push(...tagLines(before[field], after[field]));
      continue;
    }
    // 켜고 끄는 값은 화살표 없이 결과만 말한다 — "목록에 보임 → 목록에서 숨김" 은 같은 말을 두 번 한다.
    if (field === 'hidden') {
      lines.push(valueText(field, after[field] ?? false, lookup));
      continue;
    }
    lines.push(`${fieldLabel(field)}: ${valueText(field, before[field], lookup)} → ${valueText(field, after[field], lookup)}`);
  }

  return lines;
}

/**
 * 동작 자체가 이미 내용인 것들.
 *
 * 바로 보내기·페어링·삭제는 "필드가 이렇게 바뀌었다" 로 설명되지 않는다.
 * 사람이 한 일의 모양대로 적는다.
 */
function actionLines(entry: AuditEntry, lookup?: NameLookup): string[] | null {
  const after = asRecord(entry.after);
  const before = asRecord(entry.before);

  switch (entry.action) {
    case 'display.direct-send':
    case 'display.direct-send-bulk': {
      const count = typeof after.assets === 'number' ? after.assets : null;
      const lines: string[] = [count === null ? '콘텐츠를 바로 내보냈습니다' : `콘텐츠 ${count}개를 바로 내보냈습니다`];
      lines.push(after.until ? `${timeText(after.until)}까지` : '해제할 때까지 유지');
      if (typeof after.note === 'string' && after.note) lines.push(`메모: ${after.note}`);
      return lines;
    }
    case 'display.direct-clear': {
      const lines = ['바로 보내기를 해제했습니다 — 원래 편성으로 돌아갑니다'];
      if (typeof before.note === 'string' && before.note) lines.push(`해제한 전송 메모: ${before.note}`);
      return lines;
    }
    case 'display.pair':
    case 'display.claim': {
      const lines = ['이 화면을 등록했습니다'];
      for (const field of ['name', 'location', 'region'] as const) {
        if (after[field]) lines.push(`${fieldLabel(field)}: ${valueText(field, after[field], lookup)}`);
      }
      return lines;
    }
    case 'display.rotate-token':
      return ['디바이스 토큰을 새로 발급했습니다 — 이전 토큰은 10분 뒤 막힙니다'];
    case 'display.delete':
      return [`화면을 삭제했습니다${before.name ? ` — ${String(before.name)}` : ''}`];
    case 'display.maintenance-pin': {
      // PIN 값은 서버가 남기지 않는다. 무엇을 했는지와 몇 대였는지만 말한다.
      const lines = [
        after.cleared
          ? '콘솔 PIN 을 지웠습니다 — 기기 설정의 PIN 으로 돌아갑니다'
          : '정비 모드 PIN 을 새로 정했습니다',
      ];
      if (typeof after.requested === 'number' && after.requested > 1) lines.push(`${after.requested}대에 적용`);
      return lines;
    }
    case 'display.agent-settings': {
      // 보낸 값만 남는다 — 빠진 설정은 바꾸지 않은 것이다.
      const choice = (value: unknown): string => (value === true ? '켜기' : value === false ? '끄기' : '기기에 맡김');
      const lines: string[] = [];
      if ('auto_update' in after) lines.push(`자동 업데이트: ${choice(after.auto_update)}`);
      if ('autostart' in after) lines.push(`부팅 시 자동 실행: ${choice(after.autostart)}`);
      if (typeof after.requested === 'number' && after.requested > 1) lines.push(`${after.requested}대에 적용`);
      return lines;
    }
    case 'display.reboot':
      return ['기기 재부팅 명령을 보냈습니다 — 화면이 받아 다시 시작합니다'];
    case 'display.quit':
      return ['앱 종료 명령을 보냈습니다 — 현장에서 다시 켜기 전까지 화면이 꺼져 있습니다'];
    case 'display.power-off':
      return ['전원 종료 명령을 보냈습니다 — 현장에서 전원을 넣기 전까지 기기가 꺼져 있습니다'];
    case 'display.exit-maintenance':
      return ['정비 모드 해제 명령을 보냈습니다 — 화면이 받아 정비 화면을 닫고 재생으로 돌아갑니다'];
    default:
      return null;
  }
}

/**
 * 이력 한 줄에 붙일 설명.
 *
 * 빈 배열이면 붙일 말이 없다는 뜻이다(동작 이름만으로 충분한 경우) — 없는 말을
 * 지어내지 않는다.
 */
export function describeAudit(entry: AuditEntry, lookup?: NameLookup): string[] {
  return actionLines(entry, lookup) ?? diffLines(entry, lookup);
}
